import { CONSENT_FORM_STATUS, ConsentFormStatus } from '@/api/schema/consent';
import { FullPatient, Patient } from '@/api/queries/patients';
import { API_ERROR_KEYS, ApiErrorMessage } from '@/types/errors';
import { clsx, type ClassValue } from 'clsx';
import { differenceInYears, parseISO } from 'date-fns';
import { TFunction } from 'i18next';
import { extendTailwindMerge } from 'tailwind-merge';
import { File, FusedImage } from '@/api/schema/procedures';
import JSZip from 'jszip';

const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: [
        'display-medium',
        'display-semibold',
        'heading-3xl-medium',
        'heading-3xl-semibold',
        'heading-2xl-semibold',
        'heading-2xl-bold',
        'heading-xl-semibold',
        'heading-xl-bold',
        'heading-lg-semibold',
        'heading-lg-bold',
        'heading-md-semibold',
        'heading-sm-semibold',
        'body-md-normal',
        'body-md-semibold',
        'body-sm-normal',
        'body-sm-semibold',
        'link-md',
        'link-sm',
        'button-md',
        'button-sm',
        'label-md',
        'label-sm',
        'caption-normal',
        'caption-bold',
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function refreshPage() {
  window.location.reload();
}

export const getAgeFromDob = (dob: string) => {
  const dateOfBirth = parseISO(dob);
  const age = differenceInYears(new Date(), dateOfBirth);
  return age;
};

export const isFullPatient = (patient: Patient): patient is FullPatient => {
  return 'firstName' in patient && 'lastName' in patient;
};

export const getStatusBadgeVariant = (status: ConsentFormStatus) => {
  switch (status) {
    case CONSENT_FORM_STATUS.CREATED:
    case CONSENT_FORM_STATUS.TEMPLATE:
    case CONSENT_FORM_STATUS.SENT:
      return 'warning';
    case CONSENT_FORM_STATUS.COMPLETED:
    case CONSENT_FORM_STATUS.SIGNED:
      return 'success';
    case CONSENT_FORM_STATUS.DECLINED:
      return 'destructive';
    case CONSENT_FORM_STATUS.NEW:
      return 'info';
  }
};

export const getStatusBadgeText = (status: ConsentFormStatus, t: TFunction) => {
  switch (status) {
    case CONSENT_FORM_STATUS.CREATED:
    case CONSENT_FORM_STATUS.TEMPLATE:
    case CONSENT_FORM_STATUS.SENT:
      return t('consents.statuses.pending', { ns: 'patients' });
    case CONSENT_FORM_STATUS.COMPLETED:
    case CONSENT_FORM_STATUS.SIGNED:
      return t('consents.statuses.signed', { ns: 'patients' });
    case CONSENT_FORM_STATUS.DECLINED:
      return t('consents.statuses.declined', { ns: 'patients' });
    case CONSENT_FORM_STATUS.NEW:
      return t('consents.statuses.new', { ns: 'patients' });
  }
};

export const isApiErrorMessage = (message: string): message is ApiErrorMessage => {
  return message in API_ERROR_KEYS;
};

export const parseApiError = (error: string | undefined, t: TFunction, fallbackError?: string) => {
  if (error && isApiErrorMessage(error)) {
    const translationKey = API_ERROR_KEYS[error];
    return t(`apiErrors.${translationKey}`);
  }

  return fallbackError ?? t('apiErrors.generalError');
};

export const isNotNull = <T>(x: T): x is NonNullable<T> => x != null;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

type NullToUndefined<T> = T extends null
  ? undefined
  : T extends (infer U)[]
    ? NullToUndefined<U>[]
    : T extends Record<string, unknown>
      ? { [K in keyof T]: NullToUndefined<T[K]> }
      : T;

export const nullToUndefined = <T extends Record<string, unknown>>(obj: T): NullToUndefined<T> => {
  if (Array.isArray(obj)) {
    return obj.map(nullToUndefined) as NullToUndefined<T>;
  }

  const result = {} as Record<string, unknown>;

  for (const [ key, value ] of Object.entries(obj)) {
    if (Array.isArray(value)) {
      result[key] = value.map(nullToUndefined);
    }
    else if (isObject(value)) {
      result[key] = nullToUndefined(value);
    }
    else {
      result[key] = value === null ? undefined : value;
    }
  }

  return result as NullToUndefined<T>;
};

export const isValidDownloadUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString, window.location.origin);
    // Allow only http(s) protocols and same-origin URLs
    if (url.protocol === 'http:' || url.protocol === 'https:') {
      return true;
    }
    //  Allow only same-origin relative URLs
    if (url.origin === window.location.origin) {
      return true;
    }
    return false;
  }
  catch {
    return false;
  }
};

export const handleFileDownload = (file: File | FusedImage) => {
  if (!isValidDownloadUrl(file.fileUrl)) {
    console.warn('Blocked download of invalid or untrusted file URL:', file.fileUrl);
    throw new Error('Invalid file URL');
  }

  // Create a temporary anchor element to trigger download
  const link = document.createElement('a');
  link.href = file.fileUrl;
  link.download = file.name;
  link.style.display = 'none';

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const handleFilesDownloadAsZip = async (files: File[] | FusedImage[], filename?: string) => {
  const zipFile = new JSZip();
  await Promise.all(files.map(
    async (file) => {
      if (!isValidDownloadUrl(file.fileUrl)) {
        console.warn('Blocked download of invalid or untrusted file URL:', file.fileUrl);
        throw new Error('Invalid file URL');
      }
      const response = await fetch(file.fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }
      const blob = await response.blob();
      const urlFileName = new URL(file.fileUrl).pathname.split('/').filter(Boolean).pop();
      const fileName = urlFileName ?? file.name;
      zipFile.file(fileName, blob);
    },
  ));
  const zipBlob = await zipFile.generateAsync({ type: 'blob' });

  const link = document.createElement('a');
  try {
    link.href = URL.createObjectURL(zipBlob);
    link.download = filename ?? `${Date.now()}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  finally {
    URL.revokeObjectURL(link.href);
  }
};
